/**
 * Serviço de Módulos
 * Comunicação com o backend para obter módulos de aulas
 */

import { courseHeader } from './courseContext';
import i18n from '../config/i18n';
import { env } from '../config/env';
import { authenticatedFetch, getStoredToken } from './authService';

export interface ModuleItem {
  id: number;
  name: string;
  // Timestamp da capa gerenciada no backend (null = usa mapeamento do front)
  coverUpdatedAt?: string | null;
  /** Posição na trilha. O servidor já devolve os módulos nesta ordem. */
  sortOrder?: number;
  /** #RRGGBB da capa; null ⇒ a tela usa a cor de acento do tema. */
  accentColor?: string | null;
}

export async function getModulesByUserTopic(): Promise<ModuleItem[]> {
  try {
    const response = await authenticatedFetch('/modules', {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || i18n.t('errors.fetchModules'));
    }

    const modules: ModuleItem[] = await response.json();
    return modules;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t('common.serverConnectionError'));
  }
}

export async function createModule(data: {
  name: string;
  cover?: File | null;
  sortOrder?: number;
  accentColor?: string | null;
}): Promise<ModuleItem> {
  try {
    // Com arquivo: multipart (mesmo padrão de createPdfClass). Sem arquivo: JSON.
    if (data.cover) {
      const token = getStoredToken();
      if (!token) {
        throw new Error(i18n.t('auth.userNotAuthenticated'));
      }
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('cover', data.cover);
      if (data.sortOrder != null) formData.append('sortOrder', String(data.sortOrder));
      if (data.accentColor) formData.append('accentColor', data.accentColor);

      const response = await fetch(`${env.apiBaseUrl}/modules`, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Requested-With': 'XMLHttpRequest',
          // Upload não passa pelo authenticatedFetch (o Content-Type é do
          // FormData), então o curso da requisição vai daqui.
          ...courseHeader(),
        },
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Erro ao criar módulo');
      }
      return await response.json();
    }

    const response = await authenticatedFetch('/modules', {
      method: 'POST',
      body: JSON.stringify({
        name: data.name,
        ...(data.sortOrder != null ? { sortOrder: data.sortOrder } : {}),
        ...(data.accentColor ? { accentColor: data.accentColor } : {}),
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erro ao criar módulo');
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t('common.serverConnectionError'));
  }
}

export async function updateModule(
  id: number,
  data: {
    name: string;
    cover?: File | null;
    removeCover?: boolean;
    sortOrder?: number;
    accentColor?: string | null;
  },
): Promise<ModuleItem> {
  try {
    if (data.cover || data.removeCover) {
      const token = getStoredToken();
      if (!token) {
        throw new Error(i18n.t('auth.userNotAuthenticated'));
      }
      const formData = new FormData();
      formData.append('name', data.name);
      if (data.cover) formData.append('cover', data.cover);
      if (data.removeCover) formData.append('removeCover', 'true');
      if (data.sortOrder != null) formData.append('sortOrder', String(data.sortOrder));
      if (data.accentColor) formData.append('accentColor', data.accentColor);

      const response = await fetch(`${env.apiBaseUrl}/modules/${id}`, {
        method: 'PATCH',
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Requested-With': 'XMLHttpRequest',
          // Upload não passa pelo authenticatedFetch (o Content-Type é do
          // FormData), então o curso da requisição vai daqui.
          ...courseHeader(),
        },
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Erro ao atualizar módulo');
      }
      return await response.json();
    }

    const response = await authenticatedFetch(`/modules/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        name: data.name,
        ...(data.sortOrder != null ? { sortOrder: data.sortOrder } : {}),
        ...(data.accentColor ? { accentColor: data.accentColor } : {}),
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erro ao atualizar módulo');
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t('common.serverConnectionError'));
  }
}

export async function deleteModule(id: number): Promise<void> {
  try {
    const response = await authenticatedFetch(`/modules/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erro ao excluir módulo');
    }
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t('common.serverConnectionError'));
  }
}
